import { Badge, CriticalIcon, VisuallyHidden } from '@buffer-mono/popcorn'

import { formatCompactNumber } from '~publish/helpers/numberFormatters'

import type { MainSidebarChannel } from './useChannels'

import styles from './ChannelItemSuffix.module.css'

import type { JSX } from 'react'

type ChannelItemSuffixProps = {
  channel: MainSidebarChannel
  isExpanded: boolean
  queueCount: number
  queueCountLimit: number | undefined
  communityCount: number
  /** Whether `communityCount` includes mentions (drives the a11y label). */
  includesMentions: boolean
}

/**
 * Checks if a Twitter/X channel requires refreshing due to API migration.
 */
function requiresRefreshing(channel: MainSidebarChannel): boolean {
  return channel.service === 'twitter' && channel.apiVersion !== '2'
}

/**
 * Renders the suffix content for a channel item in the sidebar.
 *
 * Non-interactive indicators only: `Sidebar.Button` renders its suffix inside
 * its own root, so anything focusable here would nest inside the channel link.
 *
 * When a channel has an error state (disconnected or requires refreshing),
 * displays a warning icon.
 *
 * When the channel sub-nav is collapsed, shows the queue count badge and,
 * when enabled via preference, the community count badge.
 */
export function ChannelItemSuffix({
  channel,
  isExpanded,
  queueCount,
  queueCountLimit,
  communityCount,
  includesMentions,
}: ChannelItemSuffixProps): JSX.Element {
  const isDisconnected = channel.isDisconnected
  const needsRefresh = requiresRefreshing(channel)
  const hasError = isDisconnected || needsRefresh

  // TODO: Replace hard coded false with user account.preference value when channel badge settings are implemented
  // This change will be implemented in ticket: CORE-4295
  const shouldDisplayCommunityCount = false

  const hasBadges = !isExpanded && !hasError
  /* we cap the community count at 99 to avoid overflow */
  const displayCommunityCount = communityCount >= 100 ? '99+' : communityCount

  return (
    <div className={styles.suffix}>
      {hasError && (
        <span
          className={styles.errorIndicator}
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The named channel error indicator is rendered from an icon component, not an img source.
          role="img"
          aria-label="There is a problem with this channel, you may need to refresh it."
        >
          <CriticalIcon color="critical" />
        </span>
      )}
      {hasBadges && (
        <span className={styles.badges}>
          {shouldDisplayCommunityCount && communityCount > 0 && (
            <Badge size="small" className={styles.communityBadge}>
              {displayCommunityCount}
              <VisuallyHidden>
                {includesMentions
                  ? ' unanswered comments and mentions'
                  : ' unreplied comments'}
              </VisuallyHidden>
            </Badge>
          )}
          <span className={styles.queueCount}>
            {formatCompactNumber(queueCount, queueCountLimit)}
            <VisuallyHidden> scheduled posts</VisuallyHidden>
          </span>
        </span>
      )}
    </div>
  )
}
